import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 6;
const INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null, message: "Checking your payment…" });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", message: "Missing session id." });
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ phase: "success", data, message: "Payment received." });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "error", message: "Session expired. Please try again." });
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState({ phase: "pending", message: "Still processing. We'll email you when it clears." });
          return;
        }
        setTimeout(poll, INTERVAL);
      } catch {
        if (cancelled) return;
        setState({ phase: "error", message: "Could not verify payment." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
      <div className="max-w-md w-full rounded-[36px] bg-card/40 border border-border/60 p-10 text-center">
        {state.phase === "checking" && (
          <>
            <Loader2 className="mx-auto mb-6 animate-spin text-secondary" size={36} />
            <h1 className="font-serif text-3xl">Confirming your payment</h1>
            <p className="text-muted-foreground mt-3">{state.message}</p>
          </>
        )}

        {state.phase === "success" && (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-primary/40 flex items-center justify-center mb-6 check-circle">
              <svg width="40" height="40" viewBox="0 0 50 50" fill="none">
                <path d="M14 26 L22 34 L37 18" stroke="hsl(var(--foreground))" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="check-path" />
              </svg>
            </div>
            <h1 className="font-serif text-3xl">Thank you — your session is confirmed</h1>
            <p className="text-muted-foreground mt-3">A confirmation is on its way to your inbox. We can't wait to welcome you.</p>
          </>
        )}

        {state.phase === "pending" && (
          <>
            <h1 className="font-serif text-3xl">Almost there</h1>
            <p className="text-muted-foreground mt-3">{state.message}</p>
          </>
        )}

        {state.phase === "error" && (
          <>
            <h1 className="font-serif text-3xl">Hmm, something's off</h1>
            <p className="text-muted-foreground mt-3">{state.message}</p>
          </>
        )}

        <Link to="/" className="block mt-8">
          <Button data-testid="back-home-btn" className="rounded-full px-8 py-5 bg-foreground text-background hover:bg-foreground/85">
            Back to Epic Heal
          </Button>
        </Link>
      </div>
    </div>
  );
}
